// Shared visual vocabulary for the Domain Explorer (graph, cost breakdown,
// summary) — mirrors the prototype demo's layer model.

// The type→category map and cost/provenance readers live in @lizrd/contracts so the
// API cost aggregate and the web cost views share one source (no drift). Re-exported
// here so existing `graph-visuals` imports keep working unchanged.
export {
  TYPE_CATEGORY,
  categoryOf,
  costOf,
  costProvenanceOf,
  type CostProvenance,
} from '@lizrd/contracts';

export type Layer = 'code' | 'iac' | 'cloud' | 'runtime' | 'other';

export const TYPE_LAYER: Record<string, Layer> = {
  repository: 'code',
  'code.application': 'code',
  'code.dependency': 'code',
  'container.image': 'code',
  'iac.resource': 'iac',
  'cloud.account': 'cloud',
  'compute.instance': 'cloud',
  'compute.scaleset': 'cloud',
  'compute.function': 'cloud',
  'ml.endpoint': 'cloud',
  'ml.notebook': 'cloud',
  'ml.model': 'cloud',
  'storage.volume': 'cloud',
  'storage.snapshot': 'cloud',
  'storage.bucket': 'cloud',
  'cache.cluster': 'cloud',
  'network.nat': 'cloud',
  'network.address': 'cloud',
  'network.loadbalancer': 'cloud',
  'network.endpoint': 'cloud',
  'network.gateway': 'cloud',
  'network.cdn': 'cloud',
  database: 'cloud',
  'warehouse.instance': 'cloud',
  'search.cluster': 'cloud',
  'streaming.cluster': 'cloud',
  'storage.filesystem': 'cloud',
  'compute.workload': 'runtime',
  'container.cluster': 'runtime',
  'container.workload': 'runtime',
};
export const layerOf = (type: string): Layer => TYPE_LAYER[type] ?? 'other';

export const LAYER_COLOR: Record<Layer, string> = {
  code: '#22d3ee',
  iac: '#a78bfa',
  cloud: '#34d399',
  runtime: '#fbbf24',
  other: '#8b93a7',
};
export const LAYER_LABEL: Record<Layer, string> = {
  code: 'Code',
  iac: 'Infrastructure-as-Code',
  cloud: 'Cloud',
  runtime: 'Runtime',
  other: 'Other',
};
export const PROVIDER_ACCENT: Record<string, string> = {
  AWS: '#f0a35e',
  AZURE: '#5aa9f0',
  GCP: '#ef6f6f',
  GITHUB: '#22d3ee',
  GITLAB: '#fc6d26',
  SANDBOX: '#8b93a7',
};

/**
 * A short, human-recognisable identifier for an integration, derived from its
 * non-secret config — so two connections of the same provider (e.g. two AWS
 * accounts) are distinguishable at a glance. Returns null when nothing useful
 * is available. Provider-generic: add a case as new providers land.
 */
export function integrationIdentifier(integration: {
  provider: string;
  config: Record<string, unknown> | null;
}): string | null {
  const config = integration.config ?? {};
  if (integration.provider === 'AWS') {
    const roleArn = typeof config.roleArn === 'string' ? config.roleArn : '';
    const account = roleArn.match(/arn:aws:iam::(\d{12}):/)?.[1];
    return account ? `acct ${account}` : null;
  }
  if (integration.provider === 'GITHUB') {
    return typeof config.repository === 'string' ? config.repository : null;
  }
  if (integration.provider === 'GITLAB') {
    if (typeof config.groupPath === 'string' && config.groupPath) return config.groupPath;
    const base = typeof config.baseUrl === 'string' ? config.baseUrl : '';
    try {
      return base ? new URL(base).host : null;
    } catch {
      return null;
    }
  }
  return null;
}

// Node-type and cost-category icons now live in components/node-icons
// (NodeTypeIcon / CostCategoryIcon) — the Bedrock lucide icon set replaced the
// emoji that used to live here.

/** Category → a distinct on-brand hue for treemap / cost-flow fills. */
export const CATEGORY_COLOR: Record<string, string> = {
  Compute: '#1f8a5c', // brand green (Bedrock)
  Databases: '#22d3c4', // teal
  Cache: '#f0b429', // amber
  Storage: '#3ba7e8', // azure
  Networking: '#a78bfa', // violet
  Analytics: '#e879a6', // magenta
  'AI / ML': '#c084fc', // bright purple — accelerated/AI workloads
  Account: '#8b93a7', // muted
};
export const categoryColor = (category: string): string => CATEGORY_COLOR[category] ?? '#8b93a7';

/** Implementation-effort level → a short human label + coarse time band (EST). The
 * label is deliberately vague (effort is guidance, not a guaranteed duration). */
export const EFFORT_META: Record<string, { label: string; band: string }> = {
  very_low: { label: 'Very low', band: '≈15 min' },
  low: { label: 'Low', band: '≈1 hour' },
  medium: { label: 'Medium', band: '≈half a day' },
  high: { label: 'High', band: '≈1 day' },
  very_high: { label: 'Very high', band: 'multi-day' },
};
/** e.g. "Low (≈1 hour)". Falls back gracefully for unknown levels. */
export const effortLabel = (level: string): string => {
  const m = EFFORT_META[level];
  return m ? `${m.label} (${m.band})` : level;
};

/** Priority tier → label + brand color (uses the same token hues as severity). */
export const PRIORITY_META: Record<string, { label: string; color: string }> = {
  high: { label: 'High', color: 'var(--danger)' },
  medium: { label: 'Medium', color: 'var(--warning)' },
  low: { label: 'Low', color: 'var(--iris)' },
};
/** Priority tier rank for client-side sorting (higher = more important). */
export const PRIORITY_RANK: Record<string, number> = { high: 3, medium: 2, low: 1 };

/** A minimal graph shape the cost views need (decoupled from the DTO). */
type GraphLike = { nodes: { id: string; type: string }[]; edges: { from: string; to: string }[] };

/**
 * Nearest repository for every node (multi-source BFS over undirected edges) —
 * a resource's owning code base for cost attribution. Nodes no repo can reach
 * are "unattributed".
 */
export function nearestRepo(graph: GraphLike): Map<string, string> {
  const adj = new Map<string, string[]>();
  for (const e of graph.edges) {
    (adj.get(e.from) ?? adj.set(e.from, []).get(e.from)!).push(e.to);
    (adj.get(e.to) ?? adj.set(e.to, []).get(e.to)!).push(e.from);
  }
  const owner = new Map<string, string>();
  const queue: string[] = [];
  for (const n of graph.nodes)
    if (n.type === 'repository') {
      owner.set(n.id, n.id);
      queue.push(n.id);
    }
  for (let i = 0; i < queue.length; i++)
    for (const nb of adj.get(queue[i]!) ?? [])
      if (!owner.has(nb)) {
        owner.set(nb, owner.get(queue[i]!)!);
        queue.push(nb);
      }
  return owner;
}

/** Friendly "what it is" label per canonical type, per provider. Types are
 * provider-neutral, so the display name is resolved from the resource's provider
 * (an AWS bucket → "S3 bucket", a GCP bucket → "Cloud Storage bucket"). */
export const TYPE_LABEL: Record<string, string> = {
  'compute.instance': 'EC2 instance',
  'compute.scaleset': 'VM Scale Set',
  'compute.function': 'Lambda function',
  // Neutral labels — managed-ML types read correctly across AWS/GCP/Azure (the provider
  // badge disambiguates the cloud); avoids per-cloud label drift for the new AI-0 types.
  'ml.endpoint': 'Inference endpoint',
  'ml.notebook': 'ML notebook',
  'ml.model': 'Managed model',
  'compute.workload': 'ECS service',
  database: 'Database',
  'warehouse.instance': 'Redshift warehouse',
  'cache.cluster': 'ElastiCache cluster',
  'storage.volume': 'EBS volume',
  'storage.snapshot': 'EBS snapshot',
  'storage.bucket': 'S3 bucket',
  'network.nat': 'NAT gateway',
  'network.address': 'Elastic IP',
  'network.loadbalancer': 'Load balancer',
  'network.endpoint': 'VPC endpoint',
  'network.gateway': 'Transit gateway',
  'container.cluster': 'EKS cluster',
  'container.workload': 'Kubernetes workload',
  'search.cluster': 'OpenSearch domain',
  'streaming.cluster': 'MSK cluster',
  'storage.filesystem': 'File system',
  'network.cdn': 'CloudFront distribution',
  'cloud.account': 'Account',
};
/** GCP-flavoured labels for the same canonical types. */
export const GCP_TYPE_LABEL: Record<string, string> = {
  'compute.instance': 'Compute Engine instance',
  'compute.function': 'Cloud Function',
  'compute.workload': 'Cloud Run service',
  'ml.endpoint': 'Inference endpoint',
  'ml.notebook': 'ML notebook',
  'ml.model': 'Managed model',
  database: 'Cloud SQL instance',
  'warehouse.instance': 'BigQuery reservation',
  'cache.cluster': 'Memorystore instance',
  'storage.volume': 'Persistent Disk',
  'storage.snapshot': 'Disk snapshot',
  'storage.bucket': 'Cloud Storage bucket',
  'network.nat': 'Cloud NAT gateway',
  'network.address': 'Static IP address',
  'network.loadbalancer': 'Load balancer',
  'network.endpoint': 'Private Service Connect endpoint',
  'network.gateway': 'Cloud Router',
  'container.cluster': 'GKE cluster',
  'container.workload': 'Kubernetes workload',
  'cloud.account': 'Project',
};
export const typeLabel = (type: string, provider?: string): string =>
  provider === 'GCP' ? (GCP_TYPE_LABEL[type] ?? type) : (TYPE_LABEL[type] ?? type);

/** Minimal resource shape needed to build a provider-console deep link. */
export interface ConsoleTarget {
  type: string;
  provider: string;
  name: string;
  externalId: string;
  properties: Record<string, unknown>;
}

/**
 * A deep link to the resource in its cloud provider's console, or null. AWS only
 * for now (Azure/GCP return null until those providers land). Ids are parsed from
 * the resource ARN; a few list-only services land on the service page.
 */
export function consoleUrl(node: ConsoleTarget): string | null {
  if (node.provider !== 'AWS') return null;
  const region = typeof node.properties.region === 'string' ? node.properties.region : 'us-east-1';
  const ext = node.externalId;
  const tail = (sep: string) => ext.split(sep).pop() ?? '';
  const ec2 = (frag: string) => `https://console.aws.amazon.com/ec2/home?region=${region}#${frag}`;
  switch (node.type) {
    case 'compute.instance':
      return ec2(`InstanceDetails:instanceId=${tail('/')}`);
    case 'storage.volume':
      return ec2(`VolumeDetails:volumeId=${tail('/')}`);
    case 'storage.snapshot':
      return ec2(`SnapshotDetails:snapshotId=${tail('/')}`);
    case 'network.nat':
      return `https://console.aws.amazon.com/vpcconsole/home?region=${region}#NatGatewayDetails:natGatewayId=${tail('/')}`;
    case 'network.address':
      return ec2('Addresses:');
    case 'network.loadbalancer':
      return ec2('LoadBalancers:');
    case 'database':
      return `https://console.aws.amazon.com/rds/home?region=${region}#database:id=${tail(':')};is-cluster=false`;
    case 'warehouse.instance':
      return `https://console.aws.amazon.com/redshiftv2/home?region=${region}#cluster-details?cluster=${node.name}`;
    case 'compute.workload': {
      const parts = ext.split('/');
      const service = parts.pop() ?? '';
      const cluster = parts.pop() ?? '';
      return `https://console.aws.amazon.com/ecs/v2/clusters/${cluster}/services/${service}?region=${region}`;
    }
    case 'cache.cluster':
      return `https://console.aws.amazon.com/elasticache/home?region=${region}#/dashboard`;
    case 'compute.function':
      return `https://console.aws.amazon.com/lambda/home?region=${region}#/functions/${node.name}`;
    case 'storage.bucket':
      return `https://s3.console.aws.amazon.com/s3/buckets/${node.name}`;
    default:
      return null;
  }
}

/** The cloud providers used (distinct providers among cloud/runtime nodes). */
export const CLOUD_PROVIDERS = new Set(['AWS', 'AZURE', 'GCP']);

/** The single money formatter used across the app — whole dollars, currency-aware.
 * Headline cost/spend figures read cleaner without cents. */
export const usd = (n: number, currency = 'USD'): string =>
  n.toLocaleString('en-US', { style: 'currency', currency, maximumFractionDigits: 0 });

/** A classified accelerator (GPU / AI chip) on a compute node — AI-infra optimization
 * (AI-0). Mirrors the backend `AcceleratorInfo` shape stamped during discovery. */
export interface AcceleratorSummary {
  model: string;
  family: string;
  count: number;
  vramGbPerCard: number;
  gpuClass: 'training' | 'inference';
}

/** The accelerator on a compute node, or null when it isn't GPU-accelerated. */
export const acceleratorOf = (p: Record<string, unknown>): AcceleratorSummary | null => {
  if (p.gpu !== true) return null;
  const a = p.accelerator as AcceleratorSummary | undefined;
  return a && typeof a.model === 'string' ? a : null;
};

/** Short badge label for an accelerated node, e.g. `GPU · NVIDIA A100 80GB ×8`. */
export const acceleratorLabel = (a: AcceleratorSummary): string =>
  `GPU · ${a.model}${a.count > 1 ? ` ×${a.count}` : ''}`;

/** Utilization-score (1–5) band → colour token + short label (score + explainer). */
export function scoreBand(score: number): { tone: string; label: string } {
  if (score >= 5) return { tone: 'text-iris', label: 'Excellent' };
  if (score === 4) return { tone: 'text-iris', label: 'Good' };
  if (score === 3) return { tone: 'text-bone', label: 'Fair' };
  if (score === 2) return { tone: 'text-warning', label: 'Underutilized' };
  return { tone: 'text-danger', label: 'Largely idle' };
}

/** Compact relative time, e.g. "2 min ago", or "never". */
export function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}
